import { PE_LOGO_SRC } from '../../constants/brandAssets';

/** @deprecated Prefer PE_LOGO_SRC from constants/brandAssets — alias für PEProfit. */
export const PE_PROFIT_LOGO_SRC = PE_LOGO_SRC;

export const PE_PROFIT_PAGE_CSS = `
    /* Carbon-Faser kommt aus index.css — hier nur Light-Mode neutral halten */
    html:not(.dark) .carbon-weave-bg {
        background-color: var(--pe-bg-card);
        background-image: none;
    }
`;

const PE_PROFIT_LAYOUT_RULES = `
    .pe-profit-export-mode .pe-profit-print-input,
    .pe-profit-pdf-capture .pe-profit-print-input {
        display: none !important;
    }
    .pe-profit-export-mode .pe-profit-print-value,
    .pe-profit-pdf-capture .pe-profit-print-value {
        display: block !important;
    }
    .pe-profit-export-mode .pe-profit-document,
    .pe-profit-pdf-capture .pe-profit-document {
        box-shadow: none !important;
        border: none !important;
        border-radius: 0 !important;
    }
    .pe-profit-export-mode .pe-profit-detail-grid,
    .pe-profit-pdf-capture .pe-profit-detail-grid {
        display: block !important;
    }
    .pe-profit-export-mode .pe-profit-detail-grid > *,
    .pe-profit-pdf-capture .pe-profit-detail-grid > * {
        margin-bottom: 1rem;
    }
    .pe-profit-pdf-avoid-break {
        break-inside: avoid;
        page-break-inside: avoid;
    }
    .pe-profit-export-mode .pe-profit-no-print,
    .pe-profit-pdf-capture .pe-profit-no-print {
        display: none !important;
    }
    /* KI-Bankschreiben darf über Seiten fließen — sonst Abschneiden */
    .pe-profit-banker-pitch {
        break-inside: auto;
        page-break-inside: auto;
    }
    .pe-profit-banker-pitch .pe-profit-pdf-section {
        break-inside: avoid;
        page-break-inside: avoid;
    }
`;

export const PE_PROFIT_EXPORT_CSS = `
    ${PE_PROFIT_LAYOUT_RULES}

    .pe-profit-pdf-capture .pe-profit-detail-grid > * {
        margin-bottom: 1rem;
    }
`;

const PE_PROFIT_PRINT_DOC_CSS = `
    ${PE_PROFIT_LAYOUT_RULES}

    body {
        margin: 0;
        padding: 0;
        background: #fff;
        color: #111;
    }

    @media print {
        @page {
            size: A4 portrait;
            margin: 14mm;
        }

        body {
            background: #fff !important;
        }

        .pe-profit-document {
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
            padding: 0 !important;
        }

        .pe-profit-print-input {
            display: none !important;
        }

        .pe-profit-print-value {
            display: block !important;
        }

        .pe-profit-detail-grid {
            display: block !important;
        }

        .pe-profit-detail-grid > * {
            margin-bottom: 4mm;
        }

        .pe-profit-no-print {
            display: none !important;
        }

        .pe-profit-banker-pitch {
            break-inside: auto !important;
            page-break-inside: auto !important;
        }

        .pe-profit-banker-pitch .pe-profit-pdf-section {
            break-inside: avoid;
            page-break-inside: avoid;
        }
    }
`;

/** Styles der App — ohne PE-Profit-Print-Hacks. */
export const collectPrintStyles = (): string => {
    const parts: string[] = [];

    document.querySelectorAll('link[rel="stylesheet"]').forEach((node) => {
        parts.push(node.outerHTML);
    });

    document.querySelectorAll('style').forEach((style) => {
        const text = style.textContent || '';
        if (text.includes('pe-profit-printing') || text.includes('PE_PROFIT_EXPORT_CSS')) return;
        parts.push(style.outerHTML);
    });

    return parts.join('\n');
};

export const buildPeProfitPrintHtml = (source: HTMLElement): string => {
    const clone = source.cloneNode(true) as HTMLElement;
    clone.classList.add('pe-profit-export-mode');

    return `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
${collectPrintStyles()}
<style>${PE_PROFIT_PRINT_DOC_CSS}</style>
</head>
<body>${clone.outerHTML}</body>
</html>`;
};

const waitForImages = (root: ParentNode): Promise<void> => {
    const images = Array.from(root.querySelectorAll('img'));
    if (images.length === 0) return Promise.resolve();

    return Promise.all(
        images.map(
            (img) =>
                new Promise<void>((resolve) => {
                    if (img.complete) {
                        resolve();
                        return;
                    }
                    img.addEventListener('load', () => resolve(), { once: true });
                    img.addEventListener('error', () => resolve(), { once: true });
                })
        )
    ).then(() => undefined);
};

const waitForStylesheets = (doc: Document): Promise<void> => {
    const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'));
    if (links.length === 0) return Promise.resolve();

    return Promise.all(
        links.map(
            (link) =>
                new Promise<void>((resolve) => {
                    if (link.sheet) {
                        resolve();
                        return;
                    }
                    link.addEventListener('load', () => resolve(), { once: true });
                    link.addEventListener('error', () => resolve(), { once: true });
                })
        )
    ).then(() => undefined);
};

export const loadPeProfitPrintDocument = async (
    doc: Document,
    source: HTMLElement
): Promise<void> => {
    doc.open();
    doc.write(buildPeProfitPrintHtml(source));
    doc.close();
    await waitForStylesheets(doc);
    await waitForImages(doc.body);
};

export const printPeProfitReport = (source?: HTMLElement | null): void => {
    const element = source ?? document.getElementById('pe-profit-report-export');
    if (!element) return;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'PE Profit Druck');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument;
    if (!doc) {
        iframe.remove();
        return;
    }

    const runPrint = async () => {
        try {
            await loadPeProfitPrintDocument(doc, element);
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
        } finally {
            window.setTimeout(() => iframe.remove(), 1000);
        }
    };

    void runPrint();
};
